import useOptimisticSetProposalContentSection from '@/api/mutations/proposals/optimistic/useOptimisticSetProposalContentSection';
import { ProposalContentSectionQuery } from '@/api/queries/proposal/useGetProposal';
import AutoSaveForm from '@/ui/components/autoSaveForm/AutoSaveForm';
import { Field, RichTextarea, RichTextContent } from '@payaca/ambrosia';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

export type TProps = {
  proposalId: string;
  section: ProposalContentSectionQuery;
};

type FormValues = {
  content: RichTextContent;
};

/**
 * Carried over from the previous builder, which prompts for the same two
 * sections. A section key with no entry falls back to the generic prompt.
 */
const SECTION_PLACEHOLDERS: Record<string, string> = {
  intro:
    'Start your proposal off with a personalised message, greet your customer by their first name.',
  notes:
    'Give yourself an edge over your competitors by adding additional information to your proposal.',
};

const RichtextContentTab: FC<TProps> = (props) => {
  const { proposalId, section } = props;

  /**
   * mutations
   */
  const { mutate: setProposalContentSection } =
    useOptimisticSetProposalContentSection({
      proposalId,
    });

  /**
   * form state
   */
  // Seed once from the section content. The builder page keys this tab on a
  // revision it bumps only when something other than this editor writes the
  // content — applying a Template, or Agent Dave — so the tab remounts onto the
  // new content while the autosave's own refetch can never clobber typing.
  const methods = useForm<FormValues>({
    defaultValues: {
      content: section.content as RichTextContent,
    },
  });

  return (
    <AutoSaveForm
      className="px-3.5 h-full"
      methods={methods}
      getComparable={({ content }) => JSON.stringify(content)}
      onSubmit={({ content }) => {
        setProposalContentSection({
          proposalId,
          sectionKey: section.key,
          content,
        });
      }}
    >
      <Field name="content" className="h-full">
        <Field.Label className="sr-only">{section.title}</Field.Label>

        <Controller<FormValues, 'content'>
          name="content"
          render={({ field }) => (
            <div className="h-full [&>*]:h-full">
              <RichTextarea
                className="h-full"
                value={field.value}
                onChange={field.onChange}
                borderless
                placeholder={
                  SECTION_PLACEHOLDERS[section.key] ?? 'Write something...'
                }
                aria-label={section.title ?? undefined}
              />
            </div>
          )}
        />
      </Field>
    </AutoSaveForm>
  );
};

export default RichtextContentTab;
